/**
 * Shared PlumX "see details" link builder (RDPNEXUS-883 AR-2.3).
 *
 * Extracted from the widget so the widget and the artifact-page widget builder
 * produce identical links. This module is intentionally decoupled: it does NOT
 * import `WidgetConfig`/`CATEGORIES` from `@plumx/widget`, and it does NOT reuse
 * the app-facing `IdentifierType` union (a different set). `packages/utils` is a
 * pure `tsc` package with no `vite/client` types, so the base URL is passed in by
 * callers (the widget/app resolve `VITE_DETAILS_LINK_BASE_URL` themselves).
 */

/**
 * Identifier schemes accepted by `buildDetailsLink`, keyed by the exact PlumX
 * query-param name each maps to. Mirrors the widget's supported identifier union.
 * Derived from `DETAILS_LINK_IDENTIFIER_TYPES` so the type and runtime set stay
 * in sync.
 */
const DETAILS_LINK_IDENTIFIER_TYPES = [
  'plumx_id',
  'doi',
  'isbn',
  'bepress_url_id',
  'arxiv',
  'github_repo_id',
  'oclc',
  'nct_id',
  'pmid',
  'pmcid',
  'slideshare_slideshow_id',
  'sourceforge_repo_id',
  'ssrn_id',
  'us_patent_publication_id',
  'vimeo_video_id',
  'youtube_video_id',
  'medwave_id',
  'pitt_eprint_dscholar_id',
  'smithsonian_pddr_id',
  'lccn',
  'masid',
  'repo_url',
  'url_id',
  'cabi_abstract_id',
  'ebsco_db_an_match_id',
  'elsevier_id',
  'scopus_id',
  'airiti_doc_id',
  'airiti_book_id',
  'cscd_id',
  'mendeley_data_id',
  'datasearch_id',
  'orcid',
  'plum_id',
] as const;

export type DetailsLinkIdentifierType = (typeof DETAILS_LINK_IDENTIFIER_TYPES)[number];

/** Runtime check that a string is a valid `DetailsLinkIdentifierType`. */
export const isDetailsLinkIdentifierType = (value: string): value is DetailsLinkIdentifierType =>
  (DETAILS_LINK_IDENTIFIER_TYPES as ReadonlyArray<string>).includes(value);

/**
 * Options for `buildDetailsLink`. Self-contained (no `@plumx/widget` types). The
 * five `hide<Category>` flags are the single source of truth for the query-param
 * names appended when the caller opts to apply its category filters to the linked
 * metrics page — a lock test keeps them aligned with the widget/artifact page.
 */
export interface DetailsLinkOptions {
  /** Base PlumX URL; callers resolve `VITE_DETAILS_LINK_BASE_URL`. Defaults to `https://plu.mx/plum/a/`. */
  detailsLinkBaseUrl?: string;
  /** When true, append the set `hide<Category>` flags as query params (RDPNEXUS-836). */
  applyFiltersToMetricsPage?: boolean;
  hideUsage?: boolean;
  hideCaptures?: boolean;
  hideMentions?: boolean;
  hideSocialMedia?: boolean;
  hideCitations?: boolean;
}

const DEFAULT_BASE_URL = 'https://plu.mx/plum/a/';

/** The `hide<Category>` param names — the shared contract with the artifact page. */
const HIDE_FLAGS = [
  'hideUsage',
  'hideCaptures',
  'hideMentions',
  'hideSocialMedia',
  'hideCitations',
] as const satisfies ReadonlyArray<keyof DetailsLinkOptions>;

/**
 * Append `hide<Category>=true` params when the caller applies its category filters
 * to the linked PlumX metrics page (RDPNEXUS-836 Feature 2).
 */
const appendFilterParams = (params: URLSearchParams, options?: DetailsLinkOptions): void => {
  if (!options?.applyFiltersToMetricsPage) {
    return;
  }
  for (const flag of HIDE_FLAGS) {
    if (options[flag]) {
      params.append(flag, 'true');
    }
  }
};

/** Construct the PlumX "see details" link for an identifier. */
export const buildDetailsLink = (
  identifier: string,
  identifierType: DetailsLinkIdentifierType,
  options?: DetailsLinkOptions,
): string => {
  const baseUrl = options?.detailsLinkBaseUrl;
  const isPlaceholder = baseUrl?.includes('PLACEHOLDER_');
  const rawUrl = (baseUrl && !isPlaceholder ? baseUrl : undefined) || DEFAULT_BASE_URL;
  const url = rawUrl.endsWith('/') ? rawUrl : `${rawUrl}/`;

  // For plumx_id, use path-based routing; append filters as a query string when present.
  // Encode the identifier consistently (filtered or not) so reserved characters (?, #, /)
  // never produce an invalid URL.
  if (identifierType === 'plumx_id') {
    const filterParams = new URLSearchParams();
    appendFilterParams(filterParams, options);
    const query = filterParams.toString();
    const encodedId = encodeURIComponent(identifier);
    return query ? `${url}${encodedId}?${query}` : `${url}${encodedId}`;
  }

  const params = new URLSearchParams();

  // Map identifier type to query parameter name (matches legacy widget behavior)
  const paramMap: Record<DetailsLinkIdentifierType, string> = {
    plumx_id: 'plumx_id',
    doi: 'doi',
    isbn: 'isbn',
    bepress_url_id: 'bepress_url_id',
    arxiv: 'arxiv',
    github_repo_id: 'github_repo_id',
    oclc: 'oclc',
    nct_id: 'nct_id',
    pmid: 'pmid',
    pmcid: 'pmcid',
    slideshare_slideshow_id: 'slideshare_slideshow_id',
    sourceforge_repo_id: 'sourceforge_repo_id',
    ssrn_id: 'ssrn_id',
    us_patent_publication_id: 'us_patent_publication_id',
    vimeo_video_id: 'vimeo_video_id',
    youtube_video_id: 'youtube_video_id',
    medwave_id: 'medwave_id',
    pitt_eprint_dscholar_id: 'pitt_eprint_dscholar_id',
    smithsonian_pddr_id: 'smithsonian_pddr_id',
    lccn: 'lccn',
    masid: 'masid',
    repo_url: 'repo_url',
    url_id: 'url_id',
    cabi_abstract_id: 'cabi_abstract_id',
    ebsco_db_an_match_id: 'ebsco_db_an_match_id',
    elsevier_id: 'elsevier_id',
    scopus_id: 'scopus_id',
    airiti_doc_id: 'airiti_doc_id',
    airiti_book_id: 'airiti_book_id',
    cscd_id: 'cscd_id',
    mendeley_data_id: 'mendeley_data_id',
    datasearch_id: 'datasearch_id',
    orcid: 'orcid',
    plum_id: 'plum_id',
  };

  const paramName = paramMap[identifierType];
  params.set(paramName, identifier);
  appendFilterParams(params, options);

  return `${url}?${params.toString()}`;
};
